/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import tutorTableData from "Views/people/tutor/data/tutorTableData";
// import TestUserTableData from "Views/TestUser/data/TestUserTableData"

import StudentLeaderBoardTableData from "Views/StudentLeaderBoard/data/StudentLeaderBoardTableData";
import { useState, useEffect } from "react";
import ArgonButton from "components/ArgonButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import { toast } from "react-toastify";
import api from "utils/api";
import {
  getStudentLeaderBoards,
  createStudentLeaderBoard,
} from "services/studentLeaderBoardService";
import PaginationBar from "components/PaginationBar/PaginationBar";

function Tables() {
  const [leaderBoardData, setLeaderBoardData] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const [formData, setFormData] = useState({
    leader1: "",
    leader2: "",
    leader3: "",
    month: "",
    year: new Date().getFullYear(),
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch students for dropdown
  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    fetchLeaderBoards(currentPage, rowsPerPage);
  }, [currentPage, rowsPerPage]);

  const fetchStudents = async () => {
    try {
      const response = await api.get("/student", {
        params: {
          limit: 10000,
          sortBy: "name",
          sortOrder: "asc",
        },
      });
      if (response.data.success) {
        const classSessionsResponse = await api.get("/classes/sessions/all", {
          params: { limit: 10000 },
        });
        const classSessions = classSessionsResponse.data.success
          ? classSessionsResponse.data.data || []
          : [];
        const activeParentIds = new Set(
          classSessions
            .filter((session) => ["ONGOING", "SCHEDULED", "CREATED"].includes(session.status))
            .map((session) => session?.parentId?._id || session?.parentId)
            .filter(Boolean)
            .map(String)
        );

        const activeParentStudents = (response.data.data || []).filter((student) => {
          const parentId = student?.parentId?._id || student?.parentId;
          return (
            parentId &&
            student?.parentId?.isDeleted !== true &&
            activeParentIds.has(String(parentId))
          );
        });
        setStudents(activeParentStudents);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to fetch students");
    }
  };

  const fetchLeaderBoards = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await getStudentLeaderBoards({ page, limit });
      if (response.success) {
        setLeaderBoardData(response.data);
        setTotalItems(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching leader boards:", error);
      toast.error("Failed to fetch leader boards");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (
        !formData.leader1 ||
        !formData.leader2 ||
        !formData.leader3 ||
        !formData.month ||
        !formData.year
      ) {
        toast.error("Please fill all fields");
        return;
      }

      if (
        formData.leader1 === formData.leader2 ||
        formData.leader2 === formData.leader3 ||
        formData.leader1 === formData.leader3
      ) {
        toast.error("All three leaders must be different students");
        return;
      }

      setLoading(true);
      const response = await createStudentLeaderBoard(formData);

      if (response.success) {
        toast.success("Leader board created successfully!");
        handleClose();
        fetchLeaderBoards(currentPage, rowsPerPage); // Refresh the list with current pagination
      }
    } catch (error) {
      console.error("Error creating leader board:", error);
      toast.error(error.message || "Failed to create leader board");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leader1: "",
      leader2: "",
      leader3: "",
      month: "",
      year: new Date().getFullYear(),
    });
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const { columns, rows } = StudentLeaderBoardTableData(leaderBoardData);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Student leader Board
              </ArgonTypography>
              <ArgonButton size="small" onClick={handleOpen} variant="contained" color="primary">
                Add
              </ArgonButton>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching leader boards...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : leaderBoardData.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    No leader boards found
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Try adding a new leader board entry.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {leaderBoardData.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        {/* Header */}
        <DialogTitle>
          <ArgonTypography variant="h5" fontWeight="bold" color="dark">
            Add Student Leader Board
          </ArgonTypography>
        </DialogTitle>

        {/* Body */}
        <DialogContent>
          <ArgonBox py={2}>
            <Grid container spacing={3}>
              {/* Month */}
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Month
                </ArgonTypography>
                <Dropdown
                  value={formData.month}
                  options={[
                    { value: "January", label: "January" },
                    { value: "February", label: "February" },
                    { value: "March", label: "March" },
                    { value: "April", label: "April" },
                    { value: "May", label: "May" },
                    { value: "June", label: "June" },
                    { value: "July", label: "July" },
                    { value: "August", label: "August" },
                    { value: "September", label: "September" },
                    { value: "October", label: "October" },
                    { value: "November", label: "November" },
                    { value: "December", label: "December" },
                  ]}
                  onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                  width={550}
                  placeholder="Select Month"
                />
              </Grid>

              {/* Year */}
              <Grid item xs={12} md={6}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Year
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  label="Year"
                  placeholder="Enter Year"
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                />
              </Grid>

              {/* Leader 1 */}
              <Grid item xs={12}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Leader 1 (1st Rank)
                </ArgonTypography>
                <Dropdown
                  value={formData.leader1}
                  options={students.map((student) => ({
                    value: student._id,
                    label: `${student.name}`,
                  }))}
                  onChange={(e) => setFormData({ ...formData, leader1: e.target.value })}
                  width={550}
                  placeholder="Select Student"
                />
              </Grid>

              {/* Leader 2 */}
              <Grid item xs={12}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Leader 2 (2nd Rank)
                </ArgonTypography>
                <Dropdown
                  value={formData.leader2}
                  options={students.map((student) => ({
                    value: student._id,
                    label: `${student.name}`,
                  }))}
                  onChange={(e) => setFormData({ ...formData, leader2: e.target.value })}
                  width={550}
                  placeholder="Select Student"
                />
              </Grid>

              {/* Leader 3 */}
              <Grid item xs={12}>
                <ArgonTypography variant="button" fontWeight="medium" display="block" mb={1}>
                  Leader 3 (3rd Rank)
                </ArgonTypography>
                <Dropdown
                  value={formData.leader3}
                  options={students.map((student) => ({
                    value: student._id,
                    label: `${student.name}`,
                  }))}
                  onChange={(e) => setFormData({ ...formData, leader3: e.target.value })}
                  width={550}
                  placeholder="Select Student"
                />
              </Grid>
            </Grid>

            {/* Footer Buttons */}
            <ArgonBox display="flex" justifyContent="space-between" mt={4}>
              <ArgonButton variant="outlined" color="secondary" onClick={handleClose}>
                Cancel
              </ArgonButton>
              <ArgonButton
                variant="gradient"
                color="info"
                sx={{ backgroundColor: "rgb(8, 2, 120)" }}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </ArgonButton>
            </ArgonBox>
          </ArgonBox>
        </DialogContent>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

